import axios from "axios";
import { SOURCE_IDS } from "./reportFieldCatalog";

const baseUrl = () => (process.env.REACT_APP_BACKEND_BASEURL || "").replace(/\/+$/, "");

/** Project the user has selected in the dashboard (saved by project navigation). */
export function readSelectedProjectDetails() {
  try {
    const raw = localStorage.getItem("selectedProjectDetails");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * Load the API data behind a project, one request per needed source.
 * Failed or empty sources simply stay null so their fields render empty.
 *
 * @param {object}  options
 * @param {string}  options.opportunityId   Salesforce opportunity Id of the project
 * @param {object}  options.selected        Project details already known in the dashboard (fallback)
 * @param {Function} options.getAuthConfig  From useAuthFetch()
 * @param {Set|string[]|"all"} options.sources  Which sources to load
 * @param {string}  options.preparedBy
 */
export async function loadProjectFieldContext({
  opportunityId,
  selected = {},
  getAuthConfig,
  sources = "all",
  preparedBy = "",
}) {
  const wanted = sources === "all" ? new Set(SOURCE_IDS) : new Set(sources);
  const base = baseUrl();
  const authConfig = await getAuthConfig();
  const errors = {};
  const raw = {};

  const get = async (id, url, config = authConfig) => {
    try {
      const response = await axios.get(url, config);
      return response.data ?? null;
    } catch (error) {
      errors[id] = error?.response?.status
        ? `${error.response.status} ${error.response.statusText || ""}`.trim()
        : error?.message || "Request failed";
      return null;
    }
  };

  const oppId = opportunityId || selected.id_project || selected.Id;

  // The opportunity record carries the ids every other source needs, so it is always loaded first.
  let record = null;
  if (oppId) {
    const body = await get("opportunity", `${base}/api/salesforce/opportunity/${encodeURIComponent(oppId)}`);
    record = body?.records?.[0] || null;
  }
  raw.opportunity = record;

  const ids = {
    salesforceProjectId: record?.Project__c || selected.salesforceProjectId || "",
    opportunityNo:
      record?.Opportunity_System_Wide_ID__c || selected.opportunityNo || selected.opportunityUUID || "",
    projectNo:
      record?.Project__r?.Project_System_Wide_ID__c || selected.projectId || selected.projectNo || "",
    division: record?.dv_Division_New__c || selected.division || "",
  };

  const jobs = [];
  const job = (id, enabled, url, post) => {
    if (!wanted.has(id)) return;
    if (!enabled) return;
    jobs.push(
      get(id, url).then((body) => {
        raw[id] = post ? post(body) : body;
      })
    );
  };

  job(
    "info",
    ids.salesforceProjectId,
    `${base}/api/salesforce/projects/${encodeURIComponent(ids.salesforceProjectId)}/Info`,
    (body) => (body && typeof body.records === "object" && body.records ? body.records : body)
  );
  job(
    "contacts",
    ids.opportunityNo,
    `${base}/api/contacts/opportunity/${encodeURIComponent(ids.opportunityNo)}`
  );
  job(
    "financial",
    ids.projectNo && ids.division,
    `${base}/api/finicial-budget?projectID=${encodeURIComponent(ids.projectNo)}&divisionName=${encodeURIComponent(ids.division)}`
  );
  job(
    "schedule",
    ids.opportunityNo,
    `${base}/api/schedules/${encodeURIComponent(ids.opportunityNo)}`
  );
  job(
    "outlook",
    ids.opportunityNo,
    `${base}/api/opportunity-events/${encodeURIComponent(ids.opportunityNo)}`
  );
  job(
    "onsite",
    ids.projectNo,
    `${base}/api/miter/production-performance/${encodeURIComponent(ids.projectNo)}/onsite-dates`
  );
  job(
    "production",
    ids.projectNo,
    `${base}/api/miter/production-performance/${encodeURIComponent(ids.projectNo)}`
  );

  await Promise.all(jobs);

  return {
    raw,
    selected,
    ids,
    errors,
    meta: { today: new Date(), preparedBy },
  };
}
